/**
 * What makes a guided step complete.
 *
 * Shared by the subscriber rail and the logged-out demo guide — the one piece
 * of the two systems that is genuinely generalised rather than copied. It earns
 * that because divergence here is a *silent* bug: a trigger shape that drifts
 * produces a step that simply never fires, with no error and nothing in the
 * console. Everything else the two guides have in common differs in ways that
 * are visible the moment they differ.
 *
 * It lives under src/onboarding/ because the import direction between the two
 * systems is one-way: demo-guide imports onboarding, never the reverse.
 *
 * Import-free on purpose — vitest runs environment:node, and both callers'
 * tests need this without dragging in `window`.
 */

export type StepTrigger =
  /** A trackFeatureUsage call. `featureAction` omitted = any action. */
  | { kind: "featureUsage"; featureName: string; featureAction?: string }
  /** A filter/highlight panel selection, observed via the tab store. */
  | { kind: "widgetFilters" }
  /** The rail owns this step's UI and advances it itself. */
  | { kind: "manual" };

/**
 * Does this feature_usage event satisfy the trigger?
 *
 * Only ever true for `featureUsage` triggers — the other two kinds are driven
 * by their own subscriptions and must not match here, or a `manual` step would
 * advance on the first unrelated event the app emits.
 */
export function matches(
  trigger: StepTrigger,
  event: { feature_name: string; feature_action?: string }
): boolean {
  if (trigger.kind !== "featureUsage") return false;
  if (trigger.featureName !== event.feature_name) return false;
  return (
    trigger.featureAction === undefined ||
    trigger.featureAction === event.feature_action
  );
}
